<template>
    <template v-if="!appStore.isAppEnv && isMounted">
        <component
            :is="tooltipComponent"
            v-bind="tooltipProps"
        >
            <button
                :class="[$style.button, isAnimationVisible && $style.button_animated]"
                :aria-label="buttonTitle"
                data-interface-target="install-pwa-button"
                @mouseenter="isHovered = true"
                @click="pwaStore.onInstallPWAorMSN"
            >
                <InstallButtonContent
                    v-model:isAnimationVisible="isAnimationVisible"
                    :isHovered="isHovered"
                />
            </button>
            <template #content>
                <TooltipContent
                    v-if="isDesktop"
                    :image="props.gamePic"
                    :title="$tr('toast-pwa.game_title')"
                    :description="$tr('toast-pwa.game_description')"
                >
                    <Button
                        color="purple-400"
                        size="m"
                        interfaceTarget="pwa_portal_install_game_button_click"
                        :class="$style.button_install_override"
                        target="_blank"
                        :label="$tr('toast-pwa.button_game_text')"
                        @click="pwaStore.onInstallPWAorMSN"
                    />
                </TooltipContent>
            </template>
        </component>
    </template>
</template>

<script setup lang="ts">
import { useMounted } from '@vueuse/core'
import { computed, ref } from 'vue'

import Button from '@/components/Buttons/Button.vue'
import Tooltip from '@/components/Tooltip/Tooltip.vue'
import TooltipPopup from '@/components/Tooltip/TooltipPopup.vue'
import { useTr } from '@/composables/use-tr'
import { useAppStore } from '@/store/app-store'
import { usePWAInstallStore } from '@/store/pwa-install/store'

import InstallButtonContent from './InstallButtonContent.vue'
import TooltipContent from './TooltipContent.vue'

const props = defineProps<{
    gamePic: string
    className: string
}>()

const isMounted = useMounted()
const tr = useTr()

const pwaStore = usePWAInstallStore()
const appStore = useAppStore()

const isHovered = ref(false)
const isAnimationVisible = ref(false)

const tooltipComponent = computed(() => {
    if (!isMounted.value || pwaStore.showingNativePWA) {
        return 'div'
    }
    return pwaStore.hasDeferredPrompt ? TooltipPopup : Tooltip
})

const tooltipProps = computed(() => {
    if (!isMounted.value || pwaStore.showingNativePWA) {
        return {
            class: props.className,
        }
    }
    return pwaStore.hasDeferredPrompt
        ? {
              interfaceTarget: 'pwa_portal_install_game_prompt_shown',
              triggerClass: props.className,
          }
        : {
              content: buttonTitle.value,
              triggerClass: props.className,
          }
})

const isDesktop = computed(() => appStore.platform === 'desktop')
const buttonTitle = computed(() => (isDesktop.value ? tr('gameDetails.installDesktop') : tr('gameContainer.install')))
</script>
<style module>
.button {
    color: #fff;
    background-color: var(--experimental-carbon-purple-400);
    border: 1px solid var(--white-04);
    padding: 13px 12px;
    cursor: pointer;
    line-height: 100%;
    border-radius: 1000px;
    height: 40px;
    box-sizing: border-box;
    white-space: nowrap;
    display: inline-block;
    position: relative;
}

.button:hover {
    filter: brightness(1.2);
}

.button_animated {
    overflow: hidden;
}

.button_animated::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    pointer-events: none;

    background: radial-gradient(
        117.4% 100% at 50% 0%,
        rgba(41, 41, 46, 0) 49.7%,
        rgba(100, 57, 158, 0.9) 76.56%,
        #9747ff 100%
    );

    transform: translateY(-250%);
    opacity: 0;
    animation-delay: 3s;
    animation: gradient-fall 2s cubic-bezier(0.5, 0.5, 0, 0) infinite;
}

@keyframes gradient-fall {
    0% {
        transform: translateY(-250%);
        opacity: 0;
    }
    20% {
        opacity: 1;
    }
    40% {
        transform: translateY(0%);
        opacity: 1;
    }
    60% {
        transform: translateY(120%);
        opacity: 0.6;
    }
    70% {
        transform: translateY(160%);
        opacity: 0;
    }
    100% {
        transform: translateY(160%);
        opacity: 0;
    }
}

.button_install_override {
    width: 100%;
}
</style>
